import { fmtMsg } from "@/commonui/utils/func";
import { CommonLocale, ConversationTemplateLocale, FallbackLocale } from "@/locales/localeid";
import {
	ConversationTemplateModel,
	ConversationTemplateService,
	MasterConversationType
} from "@/services/conversation-templates";
import { Component, Vue } from "vue-property-decorator";
import { Icon, Row, Col, Button, Modal, Form, Select, Input } from "ant-design-vue";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { ActionItem, MessageHelper, NotificationType } from "commonui";
import { deepClone, disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { IWorkbookCell } from "wijmo/wijmo.xlsx";
import { AccessLevel } from "@/util/enum";

interface GridTemplateModel extends ConversationTemplateModel {
	typeValue?: string;
}
@Component({
	components: {
		Grid,
		Icon,
		Row,
		Col,
		Button,
		ActionItem,
		Modal,
		Form,
		FormItem: Form.Item,
		Select,
		Option: Select.Option,
		TextArea: Input.TextArea
	}
})
export default class Fallback extends Vue {
	private templateService = ConversationTemplateService();
	public fallbacks: GridTemplateModel[] = [];
	public loading: boolean = false;
	public saving: boolean = false;
	public format = fmtMsg;
	public locale = FallbackLocale;
	public commonLocale = CommonLocale;
	public templateLocale = ConversationTemplateLocale;
	public showAddEditModal = false;
	public showDeleteModal = false;
	public form;
	public fallbackTypes = [
		{
			id: MasterConversationType.Silent,
			display: this.format(this.locale.FallbackSilent)
		},
		{
			id: MasterConversationType.Regular,
			display: this.format(this.locale.FallbackRegular)
		}
	];
	public typeLabelCol = { span: 2 };
	public selectedTemplate: ConversationTemplateModel | null = null;
	public deleteConversationId: number = 0;
	public maxTextLength = 500;
	public accessLevel = AccessLevel;
	public enableSaveButton: boolean = false;

	onEditFallback() {
		this.enableSaveButton = true;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	created() {
		this.form = this.$form.createForm(this, { name: "form" });
		this.getFallbackTemplates();
	}

	getFallbackTemplates() {
		this.loading = true;

		this.templateService
			.getTemplates({ types: [MasterConversationType.Regular, MasterConversationType.Silent] })
			.then((fallbacks) => {
				const gridTemplate: GridTemplateModel[] = [...fallbacks];
				this.fallbacks = gridTemplate.map((fallback) => {
					switch (fallback.type) {
						case 1:
							fallback.typeValue = this.format(this.locale.FallbackSilent);
							break;
						case 2:
							fallback.typeValue = this.format(this.locale.FallbackRegular);
							break;
					}
					return fallback;
				});
				this.fallbacks = fallbacks;
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}

	getTypeCellValue(cell) {
		if (!cell || !cell.item || !cell.item.type) {
			return "";
		}

		return this.getTypeValue(cell.item.type);
	}

	getTypeValue(value) {
		switch (value) {
			case MasterConversationType.Silent:
				return this.format(this.locale.FallbackSilent);

			case MasterConversationType.Regular:
				return this.format(this.locale.FallbackRegular);

			default:
				return "";
		}
	}

	onAddEditModalShow(template?: ConversationTemplateModel | null) {
		this.showAddEditModal = true;

		if (template) {
			this.selectedTemplate = template;
		} else {
			this.selectedTemplate = null;
		}
	}

	onAddEditModalCancel() {
		this.enableSaveButton = false;
		this.showAddEditModal = false;
		this.selectedTemplate = null;
	}

	onAddEdit() {
		this.form.validateFields((err, values) => {
			if (!err) {
				this.saving = true;
				if (this.selectedTemplate) {
					this.updateTemplate(values);
				} else {
					this.createTemplate(values);
				}
			}
		});
	}

	onDelete(conversationId: number) {
		this.showDeleteModal = true;
		this.deleteConversationId = conversationId;
	}

	onDeleteCancel() {
		this.showDeleteModal = false;
		this.deleteConversationId = 0;
	}

	sortByText(fallback1, fallback2) {
		if (fallback1.text < fallback2.text) {
			return -1;
		}
		if (fallback1.text > fallback2.text) {
			return 1;
		}

		return 0;
	}

	createTemplate(values) {
		this.templateService
			.createTemplate(values)
			.then((createdId) => {
				this.saving = false;
				this.showAddEditModal = false;
				const { text, type } = values;
				const createdFallback: ConversationTemplateModel = {
					id: createdId,
					text: text.trim(),
					type
				};

				this.fallbacks = this.fallbacks.concat([createdFallback]).sort(this.sortByText);

				MessageHelper.Message(NotificationType.Success, fmtMsg(this.locale.FallbackAddSuccess));
			})
			.catch(() => {
				this.saving = false;
			});
	}

	updateTemplate(values) {
		if (!this.selectedTemplate) {
			return;
		}

		const text = values.text.trim();

		this.templateService
			.updateTemplate({ id: this.selectedTemplate.id, ...values, text })
			.then(() => {
				this.saving = false;
				this.showAddEditModal = false;
				let list = deepClone(this.fallbacks);

				const index = list.findIndex((fallback) => fallback.id === this.selectedTemplate?.id);
				const editedFallback = { ...list[index], ...values, text };

				list = [...list.slice(0, index), editedFallback, ...list.slice(index + 1, list.length)].sort(
					this.sortByText
				);

				this.fallbacks = list;
				this.selectedTemplate = null;

				MessageHelper.Message(NotificationType.Success, fmtMsg(this.locale.FallbackUpdateSuccess));
			})
			.catch(() => {
				this.saving = false;
			});
	}

	deleteTemplate() {
		this.saving = true;
		this.templateService
			.deleteTemplate(this.deleteConversationId)
			.then(() => {
				this.saving = false;
				this.showDeleteModal = false;
				this.fallbacks = this.fallbacks.filter((fallback) => fallback.id !== this.deleteConversationId);
				this.deleteConversationId = 0;

				MessageHelper.Message(NotificationType.Success, fmtMsg(this.locale.FallbackDeleteSuccess));
			})
			.catch(() => {
				this.saving = false;
			});
	}

	requiredTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		value = (value || "").trim();

		if (!value) {
			callback(this.format(this.templateLocale.TemplateValidationText));
			return;
		}

		callback();
	}

	uniqueTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		let list = this.fallbacks;
		let type = this.form.getFieldValue("type");

		if (this.selectedTemplate) {
			list = list.filter((fallback) => fallback.id !== this.selectedTemplate?.id);

			if (!type) {
				type = this.selectedTemplate.type;
			}
		}

		const index = list.findIndex(
			(fallback) => fallback.type === type && fallback.text.trim().toLowerCase() === value.trim().toLowerCase()
		);

		if (index >= 0) {
			callback(fmtMsg(this.templateLocale.TemplateValidationUniqueText));
			return;
		}

		callback();
	}

	remainingCount(): number {
		var text = this.form.getFieldValue("text");
		const calculateRemainingLength = (value: string) => {
			if (!value) {
				return this.maxTextLength;
			}

			if (this.maxTextLength - value.length >= 0) {
				return this.maxTextLength - value.length;
			} else {
				return 0;
			}
		};

		if (this.selectedTemplate && !text) {
			/* 
				// initially we want to set the text value to selected template's text value.
				// typeof text is checked since the value form form comes as undefined but after user starts 
				// editing the textarea the value becomes string and we do not want to again set text's value 
				// as selected template's value.
			*/
			if (typeof text !== "string") {
				text = this.selectedTemplate.text;
			}
		}

		return calculateRemainingLength(text);
	}
}
